import React, { useState, useEffect } from "react";
import { isEmpty, cloneDeep } from "lodash";
import { createContext } from "@ali/render-engine";
import components from "./components/Remote";
import ResolverManager from "./addons/resolvers";
import getFieldApi from "./components/Form/fieldApi";
import { isComponentDisabledInDetail } from "./utils/index";

const transformComponent = (schema, detail, addons, global) => {
  const { id = "", componentName, props, children } = schema;
  if (componentName === "GroupLayout" && isEmpty(children)) {
    return;
  }
  // 编辑态设置只读，修改为 disabled
  if (detail) {
    props.isInDetail = true;
    //将详情态下需要展示禁用样式的组件设置为disabled
    //否则保持原有的readonly展示样式
    if (isComponentDisabledInDetail(componentName)) {
      props.disabled = true;
    } else {
      props.readonly = true;
    }
  } else if (!detail && props.readonly) {
    delete props.readonly;
    props.disabled = true;
  }
  // 增加空规则，确保能复原校验状态
  if (isEmpty(props.validation)) {
    props.validation = [
      {
        type: "emptyRule",
      },
    ];
  }
  Object.assign(props, addons, { global });
  const newSchema = {
    ...schema,
    id: id.split(".").join("_"),
    props,
  };
  if (children) {
    newSchema.children = children
      .map((child) => transformComponent(child, detail, addons, global))
      .filter((child) => !!child);
  }
  return newSchema;
};

const transformRealSchema = (schema, detail, addons = {}, global) => {
  const originSchema = cloneDeep(schema) || {};
  const { instanceData, pages = [], ...rest } = originSchema;
  const realPages = pages.map((page = {}) => ({
    ...page,
    layout: transformComponent(page.layout, detail, addons, global),
  }));

  return { ...rest, pages: realPages, instanceData };
};

const genFormAPI = (context, api) => {
  const getData = (options = { disableValid: false, scroll: true }) => {
    return api.getFieldsData(options);
  };

  const isValid = () => {
    const formData = context.store.getFormValue();
    return formData.success;
  };

  return {
    context,
    getData,
    isValid,
  };
};

export default function SimpleForm(props) {
  const { code, detail, params, onLoad, schema, addons, formOptions } = props;
  const formKey = `FORM_${code || "SIMPLE"}`;
  const [formContext, setFormContext] = useState(null);
  const [fieldApi, setFieldApi] = useState(null);
  const [resolverManager, setResolverManager] = useState(null);

  const realSchema = transformRealSchema(schema, detail, addons, {
    formOptions,
    params,
  });

  useEffect(() => {
    if (isEmpty(realSchema)) {
      return;
    }
    const context = createContext({
      id: formKey,
      schema: realSchema,
      locale: "zh_CN",
      whetherComponentValidation: true,
      i18nTransformWithRaw: true,
      isSectionalRender: false,
      originSchema: cloneDeep(realSchema),
      globalProps: { detail, params, formOptions },
    });

    context.setComponents(components);
    context.onPageReady(() => {
      resolverManager && resolverManager.apply();
    });
    setFormContext(context);
    const api = getFieldApi({ context });
    setFieldApi(api);

    onLoad && onLoad(genFormAPI(context, api));
    window[formKey] = context;
  }, []);

  useEffect(() => {
    if (!formContext || !fieldApi) {
      return;
    }

    const resolver = new ResolverManager({
      fieldApi,
      schema: realSchema,
      detail,
      params,
      updateRequestCounter: () => {},
    });
    if (detail) {
      resolver.parse(["rules"]);
    } else {
      resolver.parse();
      formContext.setGlobalEvents(resolver.toProps());
    }

    setResolverManager(resolver);
    formContext.render();
  }, [formContext, fieldApi]);

  return <div id={formKey} />;
}

SimpleForm.defaultProps = {
  detail: false,
  params: {},
  schema: {},
  onLoad: () => {},
};
